import { useLayoutEffect, useMemo, useState } from "react";
import { useThemeSlice } from "../../../components/ui/StatusBadge.jsx";
import { Ico } from "../../../components/icons/icons.jsx";
import { ModalShellHeader } from "../../../components/modal/ModalShellHeader.jsx";
import { buildModalShellTokens } from "../../../components/modal/modalShellTheme.js";
import { modalPrimaryButton } from "../../../components/ui/buttonStyles.js";
import { AssetModalField } from "../../Accounts/components/AssetModalField.jsx";
import { formatColumnLabel } from "../../Accounts/assetUtils.js";
import {
  SIM_FORM_EMPTY,
  SIM_STATUSES,
  simFormToPayload,
  simToForm,
  validateSimStep,
} from "../simUtils.js";

const TOTAL_STEPS = 2;
const STEP_SUBTITLES = {
  1: "Step 1 — SIM card details",
  2: "Step 2 — SIM field details",
};

export function CreateSimModal({ open, onClose, onSave, editing = null, saving = false, simTypes = [] }) {
  const { c, dark, accentHex: A } = useThemeSlice();
  const ap = useMemo(() => buildModalShellTokens(dark, c, A), [dark, c, A]);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ ...SIM_FORM_EMPTY, values: {} });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const isEdit = Boolean(editing);

  const activeSimTypes = useMemo(
    () => simTypes.filter((type) => type.status !== "deprecated"),
    [simTypes],
  );

  const selectedSimType = useMemo(
    () => simTypes.find((type) => type.id === form.simTypeId) ?? null,
    [simTypes, form.simTypeId],
  );

  useLayoutEffect(() => {
    if (!open) return;
    setStep(1);
    setForm(simToForm(editing));
    setErrors({});
    setSubmitError(null);
  }, [open, editing]);

  if (!open) return null;

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const setValue = (key, value) => {
    setForm((prev) => ({ ...prev, values: { ...prev.values, [key]: value } }));
  };

  const handleSimTypeChange = (simTypeId) => {
    const nextType = simTypes.find((type) => type.id === simTypeId);
    const nextValues = {};
    (nextType?.columns ?? []).forEach((col) => {
      nextValues[col.key] = form.values?.[col.key] ?? "";
    });
    setForm((prev) => ({ ...prev, simTypeId, values: nextValues }));
  };

  const handleNext = () => {
    const nextErrors = validateSimStep(form, 1, selectedSimType, isEdit);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setStep(2);
  };

  const handleBack = () => {
    setErrors({});
    setSubmitError(null);
    if (step === 1) {
      onClose();
      return;
    }
    setStep(1);
  };

  const handleSubmit = async () => {
    const nextErrors = validateSimStep(form, 2, selectedSimType, isEdit);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setSubmitError(null);
    try {
      await onSave(simFormToPayload(form, selectedSimType));
      onClose();
    } catch (err) {
      setSubmitError(err.message ?? "Failed to save SIM");
    }
  };

  const typeOptions = activeSimTypes.map((type) => ({ value: type.id, label: type.name }));
  const statusOptions = SIM_STATUSES.map((status) => ({ value: status, label: status }));
  const columns = selectedSimType?.columns ?? [];

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 70, background: ap.overlay, backdropFilter: "blur(8px)" }} role="presentation" />
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="sim-modal-title" style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "min(620px, 96vw)", maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden", borderRadius: 16, background: ap.cardGradient, border: `1px solid ${ap.cardBorder}`, boxShadow: ap.cardShadowLg, zIndex: 71 }}>
        <ModalShellHeader
          title={isEdit ? "Edit SIM" : "Create SIM"}
          titleId="sim-modal-title"
          subtitle={STEP_SUBTITLES[step]}
          icon={Ico.sim}
          onClose={onClose}
          closeLabel="Close SIM form"
          dark={dark}
          c={c}
          A={A}
          ap={ap}
          trailing={(
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: A, background: `${A}18`, border: `1px solid ${A}33`, borderRadius: 999, padding: "6px 10px" }}>
              {step} / {TOTAL_STEPS}
            </div>
          )}
        />
        <div style={{ padding: 24, overflowY: "auto", flex: 1, minHeight: 0 }}>
          {step === 1 && (
            <>
              {!isEdit && (
                <AssetModalField label="ICCID" value={form.iccid} onChange={(value) => set("iccid", value)} placeholder="Enter ICCID" error={errors.iccid} icon="sim" />
              )}
              <AssetModalField label="MSISDN" value={form.msisdn} onChange={(value) => set("msisdn", value)} placeholder="Enter MSISDN" error={errors.msisdn} icon="device" />
              <AssetModalField label="Carrier" value={form.carrier} onChange={(value) => set("carrier", value)} placeholder="e.g. Airtel, Jio" error={errors.carrier} icon="building" />
              <AssetModalField label="SIM type" value={form.simTypeId} onChange={handleSimTypeChange} placeholder="Select SIM type" error={errors.simTypeId} icon="layers" as="select" options={typeOptions} disabled={isEdit} />
              {activeSimTypes.length === 0 && (
                <p style={{ margin: "0 0 16px", fontSize: 12, color: c.sub, lineHeight: 1.5 }}>Create a SIM type first to define fields for new SIM cards.</p>
              )}
              <AssetModalField label="Status" value={form.status} onChange={(value) => set("status", value)} placeholder="Select status" error={errors.status} icon="model" as="select" options={statusOptions} />
            </>
          )}

          {step === 2 && (
            <>
              {columns.length === 0 ? (
                <p style={{ margin: "0 0 16px", fontSize: 12, color: c.sub, lineHeight: 1.5 }}>This SIM type has no custom fields. You can still create the SIM with the information from step 1.</p>
              ) : (
                columns.map((col) => (
                  <AssetModalField
                    key={col.key}
                    label={formatColumnLabel(col.key)}
                    value={form.values?.[col.key] ?? ""}
                    onChange={(value) => setValue(col.key, value)}
                    placeholder={`Enter ${formatColumnLabel(col.key).toLowerCase()}`}
                    error={errors.values?.[col.key]}
                    icon={col.type === "int" ? "grid" : col.type === "file" ? "layers" : "model"}
                    type={col.type === "int" ? "number" : "text"}
                  />
                ))
              )}
            </>
          )}

          {submitError && <p style={{ margin: "0 0 16px", fontSize: 12, color: "#EF4444", fontWeight: 600 }}>{submitError}</p>}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
            <button type="button" onClick={handleBack} disabled={saving} style={outlineBtn(c, dark)}>{step === 1 ? "Cancel" : "Back"}</button>
            {step < TOTAL_STEPS ? (
              <button type="button" onClick={handleNext} style={modalPrimaryButton(A, dark)}>Next</button>
            ) : (
              <button type="button" onClick={handleSubmit} disabled={saving} style={modalPrimaryButton(A, dark, saving)}>{saving ? "Saving…" : isEdit ? "Save changes" : "Create SIM"}</button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function outlineBtn(c, dark) {
  return { height: 44, padding: "0 20px", borderRadius: 10, border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`, background: "transparent", color: c.text, cursor: "pointer", fontSize: 13, fontWeight: 500 };
}
